import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Box, CircularProgress, Select, MenuItem, FormControl,
} from '@mui/material';
import PageGuideTooltip from './PageGuideTooltip';
import { PREVIOUS_BASIS, blockReasonText, isReloading, reloadingText } from '../utils/influenceBlocks';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const THRESHOLD_OPTIONS = [
  { value: 25000, label: '$25K' },
  { value: 50000, label: '$50K' },
  { value: 100000, label: '$100K' },
  { value: 250000, label: '$250K' },
];

const fmtPct = (v) => v != null ? `${(v * 100).toFixed(1)}%` : '—';

// influenceCutover: with the influence cutover ON the card reads the nightly attachment rows (qualifying deals created in the
// period, share with a TEC-trained SE on the deal) and falls back to the previous definition, labelled, when they are missing.
export default function AttachmentRateCard({ startDate, endDate, windowMonths, onClick, influenceCutover = false, canOpen = true, onThresholdChange }) {
  const [threshold, setThreshold] = useState(50000);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [influenced, setInfluenced] = useState(null);
  const [fallbackReason, setFallbackReason] = useState(null);
  const [reloading, setReloading] = useState(null);

  useEffect(() => {
    if (!startDate || !endDate || influenceCutover === null) return undefined; // null = the page does not know the cutover state yet
    let live = true;
    setLoading(true);
    setError(null);
    setInfluenced(null);
    setFallbackReason(null);
    setReloading(null);
    const legacy = () => {
      const params = new URLSearchParams({ threshold: String(threshold) });
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (windowMonths != null) params.set('windowMonths', String(windowMonths));
      if (influenceCutover) params.set('basis', 'influenced'); // service on: the API answers influenced; never the old pool beside a cutover page
      return fetch(`${API_BASE_URL}/exec/attachment-rate?${params}`, { credentials: 'include' })
        .then(res => {
          if (res.status === 403) return null;
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then(d => { if (live) { setData(d?.available === false ? null : d); setLoading(false); } });
    };
    const start = influenceCutover
      ? fetch(`${API_BASE_URL}/exec/influence/attachment?${new URLSearchParams({ startDate, endDate, threshold: String(threshold) })}`, { credentials: 'include' })
        .then(res => (res.ok ? res.json() : { available: false, reason: `HTTP ${res.status}` }))
        .catch(() => ({ available: false, reason: 'the request failed' }))
        .then(d => {
          if (d?.available) { if (live) { setInfluenced(d); setLoading(false); } return null; }
          if (isReloading(d)) { if (live) { setReloading(reloadingText(d)); setLoading(false); } return null; } // never the previous definition
          if (live) setFallbackReason(d?.reason || 'unavailable');
          return legacy();
        })
      : legacy();
    start.catch(err => { if (live) { setError(err.message); setLoading(false); } });
    return () => { live = false; };
  }, [startDate, endDate, windowMonths, threshold, influenceCutover]);

  const tooltipText = "% of qualifying Salesforce opportunities (above the $ threshold) where at least one contact attended a TEC workshop. The practitioner-standard KPI for SE program reach — are we engaging the right deals?";

  // onClick(threshold). With the influence cutover ON the click only means something when the card shows the influenced numbers
  // (plan 2213 phase 4: it lists exactly those attached deals); on the previous-definition fallback it does nothing.
  const clickable = !!onClick && (!influenceCutover || (!!influenced && canOpen));
  return (
    <Paper
      elevation={2}
      aria-label="SE Attachment Rate card"
      onClick={clickable ? () => onClick(threshold) : undefined}
      sx={{
        p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1,
        ...(clickable ? { cursor: 'pointer', '&:hover': { boxShadow: 4 } } : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
          SE Attachment Rate
        </Typography>
        <PageGuideTooltip title="SE attachment rate" description={tooltipText} metricKey="se_attachment_rate" />
      </Box>

      {loading && <CircularProgress size={24} />}

      {!loading && influenced && (
        <>
          <Typography variant="h4" sx={{ fontWeight: 700, color: influenced.rate != null && influenced.rate >= 0.5 ? '#2e7d32' : '#ed6c02' }} data-testid="attachment-influenced">
            {fmtPct(influenced.rate)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {influenced.attached?.toLocaleString()} of {influenced.qualifying?.toLocaleString()} qualifying deals had a TEC-trained SE on the deal
          </Typography>
          <Typography variant="caption" color="text.secondary" data-testid="attachment-basis">
            {influenced.label}: an SE, technical team member or named contact who attended TEC before the deal closed. Deals at or above the threshold, created in the period.
          </Typography>
        </>
      )}

      {!loading && reloading && (
        <Typography variant="caption" color="text.secondary" data-testid="attachment-reloading">{reloading}</Typography>
      )}

      {!loading && !influenced && data && (
        <>
          <Typography variant="h4" sx={{ fontWeight: 700, color: data.attachment_rate != null && data.attachment_rate >= 0.5 ? '#2e7d32' : '#ed6c02' }}>
            {fmtPct(data.attachment_rate)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {data.attached_count} of {data.total_qualifying_count} qualifying opps had SE engagement
          </Typography>
          {influenceCutover && (
            <Typography variant="caption" color="warning.main" data-testid="attachment-basis">
              Basis: {PREVIOUS_BASIS}. TEC-trained SE view unavailable: {blockReasonText(fallbackReason)}.
            </Typography>
          )}
          {!data.baseline_available && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Non-SE baseline not available
            </Typography>
          )}
        </>
      )}

      {!loading && error && (
        <Typography variant="caption" color="error">{error}</Typography>
      )}

      {!loading && !influenced && !data && !error && (
        <Typography variant="caption" color="text.secondary">No data{influenceCutover && fallbackReason ? `: ${blockReasonText(fallbackReason)}` : ''}</Typography>
      )}

      <Box sx={{ mt: 'auto', pt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
           onClick={e => e.stopPropagation()}>
        <Typography variant="caption" color="text.secondary">Threshold:</Typography>
        <FormControl size="small" variant="standard">
          <Select
            value={threshold}
            onChange={e => { setThreshold(Number(e.target.value)); onThresholdChange?.(Number(e.target.value)); }}
            sx={{ fontSize: '0.75rem' }}
          >
            {THRESHOLD_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Paper>
  );
}
